import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, Plus, Trash2, X, Youtube, Facebook, Twitch, Globe, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty } from "@/components/common";

const PROVIDER_ICON = { YouTube: Youtube, Facebook: Facebook, Twitch: Twitch, "Custom RTMP": Globe };
const PROVIDER_HINT = {
  YouTube: {
    url: "rtmps://a.rtmp.youtube.com/live2",
    steps: [
      "Open YouTube Studio → Create (top-right) → Go live.",
      "Choose Streaming software (not webcam).",
      "Copy the Stream URL: rtmps://a.rtmp.youtube.com/live2",
      "Copy your Stream key and paste both here.",
    ],
    tip: "First-time YouTube live can take up to 24h to enable; your channel must be verified with no live restrictions.",
  },
  Facebook: {
    url: "rtmps://live-api-s.facebook.com:443/rtmp/",
    steps: [
      "Go to facebook.com/live/producer (or your Page → Live).",
      "Select Go live → Streaming software.",
      "Copy the Server URL: rtmps://live-api-s.facebook.com:443/rtmp/",
      "Copy the Stream key and paste both here.",
    ],
    tip: "Use a persistent stream key if you want to reuse it. You can stream to your profile or a Page you manage.",
  },
  Twitch: {
    url: "rtmp://live.twitch.tv/app",
    steps: [
      "Open your Twitch Creator Dashboard → Settings → Stream.",
      "Copy your Primary Stream key.",
      "Use the ingest URL: rtmp://live.twitch.tv/app",
      "Paste the URL and key here.",
    ],
    tip: "Never share your Twitch stream key — anyone with it can stream to your channel.",
  },
  "Custom RTMP": {
    url: "rtmp://",
    steps: [
      "Open your platform's streaming / RTMP settings.",
      "Copy its RTMP ingest URL (must start with rtmp:// or rtmps://).",
      "Copy the stream key and paste both here.",
    ],
    tip: "Works with any RTMP target — e.g. Kick or a restreaming service.",
  },
};

function DestModal({ dest, onClose, onSaved }) {
  const editing = !!dest;
  const [provider, setProvider] = useState(dest?.provider || "YouTube");
  const [name, setName] = useState(dest?.name || "");
  const [rtmpUrl, setRtmpUrl] = useState(dest?.rtmp_url || PROVIDER_HINT["YouTube"].url);
  const [streamKey, setStreamKey] = useState("");
  const [saving, setSaving] = useState(false);

  const pickProvider = (p) => {
    setProvider(p);
    if (!editing || !rtmpUrl) setRtmpUrl(PROVIDER_HINT[p]?.url || "rtmp://");
    if (!name) setName(p);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^rtmps?:\/\//i.test(rtmpUrl.trim())) { toast.error("RTMP URL must start with rtmp:// or rtmps://"); return; }
    if (!editing && !streamKey.trim()) { toast.error("Add the stream key from your platform"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim() || provider, provider, rtmp_url: rtmpUrl.trim() };
      if (streamKey.trim()) body.stream_key = streamKey.trim();
      if (editing) await api.patch(`/simulcast/destinations/${dest.id}`, body);
      else await api.post("/simulcast/destinations", body);
      toast.success(editing ? "Destination updated" : "Destination added");
      onSaved();
    } catch (err) { toast.error(err.message || "Could not save"); }
    finally { setSaving(false); }
  };

  const hint = PROVIDER_HINT[provider];
  return (
    <div className="ss-modal-back" onClick={onClose}>
      <form className="ss-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="simulcast-dest-modal" style={{ maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h2 style={{ fontSize: 20 }}>{editing ? "Edit destination" : "Add destination"}</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose}><X size={18} /></button>
        </div>

        <label className="ss-label">Platform</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {Object.keys(PROVIDER_HINT).map((p) => {
            const Icon = PROVIDER_ICON[p] || Globe;
            return (
              <button type="button" key={p} onClick={() => pickProvider(p)} data-testid={`provider-${p}`}
                className={`ss-btn ss-btn-sm ${provider === p ? "ss-btn-primary" : "ss-btn-ghost"}`}>
                <Icon size={14} /> {p}
              </button>
            );
          })}
        </div>

        <label className="ss-label">Label</label>
        <div className="ss-field" style={{ marginBottom: 12 }}><input placeholder="e.g. My YouTube channel" value={name} onChange={(e) => setName(e.target.value)} data-testid="dest-name" /></div>

        <label className="ss-label">RTMP / stream URL</label>
        <div className="ss-field" style={{ marginBottom: 6 }}><input placeholder="rtmps://…" value={rtmpUrl} onChange={(e) => setRtmpUrl(e.target.value)} data-testid="dest-url" /></div>

        <label className="ss-label">Stream key {editing && <span className="ss-muted">(leave blank to keep current)</span>}</label>
        <div className="ss-field" style={{ marginBottom: 10 }}><input type="password" placeholder={editing ? "••••••••" : "Paste your stream key"} value={streamKey} onChange={(e) => setStreamKey(e.target.value)} data-testid="dest-key" /></div>

        {hint && (
          <div className="ss-card" style={{ padding: 12, marginBottom: 12, background: "var(--gold-soft)" }} data-testid="provider-hint">
            <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800, fontSize: 13, marginBottom: 8 }}>
              <Info size={15} style={{ flexShrink: 0 }} /> How to get your {provider} stream key
            </div>
            <ol style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 4 }}>
              {hint.steps.map((s, i) => (
                <li key={i} className="ss-muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>{s}</li>
              ))}
            </ol>
            {hint.tip && <div className="ss-muted" style={{ fontSize: 12, marginTop: 8, fontStyle: "italic" }}>Tip: {hint.tip}</div>}
          </div>
        )}

        <button className="ss-btn ss-btn-primary ss-btn-block" disabled={saving} data-testid="dest-save">{saving ? "Saving…" : (editing ? "Save changes" : "Add destination")}</button>
      </form>
    </div>
  );
}

export default function Simulcast() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null); // {} for new, dest for edit

  const load = () => api.get("/simulcast/destinations").then(setData).catch(() => setData({ destinations: [], providers: [], egress_available: false }));
  useEffect(() => { load(); }, []);
  if (data === null) return <Loading />;

  const toggle = async (d) => {
    try { await api.patch(`/simulcast/destinations/${d.id}`, { enabled: !d.enabled }); load(); }
    catch (err) { toast.error(err.message || "Could not update"); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.name}?`)) return;
    try { await api.del(`/simulcast/destinations/${d.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(err.message || "Could not remove"); }
  };

  return (
    <div className="fade-up" style={{ maxWidth: 720, margin: "0 auto" }}>
      <PageHead title="Simulcast to social" sub="Go live once on SouqStream — we restream it to your channels. YouTube, Facebook & Twitch supported." />

      <div className="ss-card" style={{ padding: 14, display: "flex", gap: 10, marginBottom: 16 }}>
        <Info size={18} style={{ flexShrink: 0, marginTop: 2, color: "var(--brand)" }} />
        <div className="ss-muted" style={{ fontSize: 13 }}>
          SouqStream is the restreamer — no third-party service needed. Add a destination, then hit
          <b> Simulcast</b> from your live show to broadcast everywhere at once.
          <br />Note: Instagram & TikTok don't allow external streaming, so they aren't supported.
          <br /><a href="/help" style={{ color: "var(--brand)", fontWeight: 700 }} data-testid="simulcast-setup-guide">See step-by-step setup for YouTube, Facebook & Twitch →</a>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontWeight: 800 }}>Your destinations</div>
        <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => setModal({})} data-testid="add-destination"><Plus size={15} /> Add destination</button>
      </div>

      {data.destinations.length === 0 ? (
        <Empty icon={Radio} title="No destinations yet" sub="Add YouTube, Facebook or Twitch to start simulcasting." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="destinations-list">
          {data.destinations.map((d) => {
            const Icon = PROVIDER_ICON[d.provider] || Globe;
            return (
              <div key={d.id} className="ss-card" style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }} data-testid={`dest-${d.id}`}>
                <div className="stat-ic"><Icon size={18} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>{d.name}</div>
                  <div className="ss-muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.rtmp_url} · key {d.stream_key_mask}</div>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={d.enabled} onChange={() => toggle(d)} data-testid={`dest-toggle-${d.id}`} />
                  {d.enabled ? "On" : "Off"}
                </label>
                <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => setModal(d)} data-testid={`dest-edit-${d.id}`}>Edit</button>
                <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)" }} onClick={() => remove(d)} data-testid={`dest-delete-${d.id}`}><Trash2 size={14} /></button>
              </div>
            );
          })}
        </div>
      )}

      {modal && <DestModal dest={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
